import { equals } from '../lodash';
import { activateConsumer, Consumer, deactivateConsumer, trackDeps } from './deps';
import { prefix } from '../../lib/logger';

const { error } = prefix('reactive-value');

const rawTargetSymbol = Symbol('rawTarget');

const whereSymbol = Symbol('where');

type SignalWhere<T> = {
  [whereSymbol]: true;
  predicate: (val: T) => boolean;
  source: SignalValue<T>;
};

export function isWhere(val: unknown): val is SignalWhere<unknown> {
  return val && val[whereSymbol];
}

export function isReactive<T>(val: unknown): val is ReactiveValue<T> {
  return val instanceof SignalValue || val instanceof ComputedValue;
}

export function isSignalValue<T>(val: unknown): val is SignalValue<T> {
  return val instanceof SignalValue;
}

export function subscribeAll(
  reactiveValues: (ReactiveValue<any> | SignalWhere<any>)[],
  callback: () => void,
  /**
   * When true, the callback will be called once in the next tick if multiple reactive values are updated.
   * Default to true
   */
  lazy: boolean = true
) {
  let queued = false;
  const queueFn = () => {
    if (!lazy) {
      callback();
    } else if (!queued) {
      queued = true;
      queueMicrotask(() => {
        queued = false;
        callback();
      });
    }
  };
  const unsubscribes = reactiveValues.map((rv) => {
    if (isWhere(rv)) {
      return rv.source.subscribe(() => {
        if (rv.predicate(rv.source.peek())) {
          queueFn();
        }
      });
    } else {
      return rv.subscribe(queueFn);
    }
  });
  return () => {
    unsubscribes.forEach((unsub) => unsub());
  };
}

export interface ReactiveValue<T> {
  value: T;
  get(): T;
  subscribe: (callback: () => void) => () => void;
  listeners: Set<Consumer>;

  dispose(): void;
}

export function createSignal<T>(initialVal: T, deep?: boolean) {
  return new SignalValue(initialVal, deep);
}

export function createComputed<T>(getter: () => T) {
  return new ComputedValue(getter);
}

export class SignalValue<T> implements ReactiveValue<T> {
  private _val: T;
  private _deep: boolean;
  private _subscribers: (() => void)[] = [];

  readonly listeners: Set<Consumer> = new Set();

  get value(): T {
    return this.get();
  }

  get() {
    trackDeps(this);
    return this._val;
  }

  peek() {
    return this._val;
  }

  set(newVal: T) {
    if (this._val === newVal) {
      return this._val;
    }

    if (this._deep) {
      this._val = this._convertFieldToSignal(newVal);
    } else {
      this._val = newVal;
    }

    [...this._subscribers].forEach((cb) => cb());
    [...this.listeners].forEach((val) => val.markDirty());

    return this._val;
  }

  where(predicate: (val: T) => boolean): SignalWhere<T> {
    return {
      [whereSymbol]: true,
      predicate,
      source: this,
    };
  }

  subscribe(callback: () => void) {
    this._subscribers.push(callback);

    return () => {
      this.unsubscribe(callback);
    };
  }

  unsubscribe(callback: () => void) {
    this._subscribers = this._subscribers.filter((cb) => cb !== callback);
  }

  constructor(initialVal: T, deep: boolean = false) {
    this._deep = deep;

    if (deep) {
      this._val = this._convertFieldToSignal(initialVal);
    } else {
      this._val = initialVal;
    }
  }

  toObject(): T {
    if (!this._deep) {
      return this._val;
    }

    return JSON.parse(JSON.stringify(this._val));
  }

  private _convertFieldToSignal(val: T) {
    if (typeof val !== 'object' || !val) {
      return val;
    }

    // Handle Set objects
    if (val instanceof Set) {
      return this._createReactiveSet(val) as T;
    }

    const toSignal = (_prop: string | symbol, propValue: unknown) => {
      return createSignal(propValue, true);
    };

    Object.entries(val).forEach(([prop, fieldVal]) => {
      (val as any)[prop] = toSignal(prop, fieldVal);
    });

    return new Proxy(val, {
      set(target, prop, newValue) {
        if (target[prop] instanceof SignalValue) {
          target[prop].set(newValue);
        } else {
          target[prop] = toSignal(prop, newValue);
        }

        return true;
      },
      get(target, prop, receiver) {
        if (prop === rawTargetSymbol) {
          return target;
        }

        const val = Reflect.get(target, prop, receiver);

        if (val instanceof SignalValue) {
          return val.value;
        }

        return val;
      },
    });
  }

  private _createReactiveSet<V>(set: Set<V>): Set<V> {
    const signalInstance = this as ReactiveValue<unknown>;

    const notify = () => {
      [...this._subscribers].forEach((cb) => cb());
      [...this.listeners].forEach((val) => val.markDirty());
    };

    return new Proxy(set, {
      get(target, prop, receiver) {
        // Return raw target for internal access
        if (prop === rawTargetSymbol) {
          return target;
        }

        // Handle size getter - needs to be bound to the original Set
        if (prop === 'size') {
          trackDeps(signalInstance);
          return Reflect.get(target, prop, target);
        }

        // Track dependency for read operations
        const shouldTrack =
          prop === 'has' ||
          prop === 'forEach' ||
          prop === 'keys' ||
          prop === 'values' ||
          prop === 'entries' ||
          prop === Symbol.iterator;

        if (shouldTrack) {
          trackDeps(signalInstance);
        }

        const value = Reflect.get(target, prop, receiver);

        // Proxy mutating methods to trigger notifications
        if (prop === 'add') {
          return function (this: Set<V>, item: V) {
            const result = target.add(item);
            notify();
            return result;
          };
        }

        if (prop === 'delete') {
          return function (this: Set<V>, item: V) {
            const result = target.delete(item);
            if (result) {
              notify();
            }
            return result;
          };
        }

        if (prop === 'clear') {
          return function (this: Set<V>) {
            if (target.size > 0) {
              target.clear();
              notify();
            }
          };
        }

        // Bind methods to the original target
        if (typeof value === 'function') {
          return value.bind(target);
        }

        return value;
      },
    });
  }

  dispose() {
    if (this._deep) {
      const val = this._val as any;
      const rawTarget = val[rawTargetSymbol];
      Object.keys(rawTarget).forEach((key) => {
        if (isReactive(rawTarget[key])) {
          rawTarget[key].dispose();
        }
      });
    }

    this._subscribers = [];
    this.listeners.clear();
  }
}

export class ComputedValue<T> implements ReactiveValue<T>, Consumer {
  private _getter: () => T;
  private _dirty: boolean = true;
  private _computing: boolean = true;
  private _cacheVal: T | undefined = undefined;

  /**
   * Dependencies that this computed value relies on
   */
  readonly deps: Set<ReactiveValue<T>> = new Set();
  readonly listeners: Set<Consumer> = new Set();

  private _setWatchers: ((oldVal: T) => void)[] = [];

  get value(): T {
    return this.get();
  }

  get() {
    trackDeps(this);
    if (this._dirty) {
      const oldVal = this._cacheVal;
      if (this._computing) {
        error('Circular dependency detected');
        return this._cacheVal as T;
      }
      const newVal = this._rebuild();
      this._cacheVal = newVal;
      this._dirty = false;

      if (!equals(oldVal, newVal)) {
        this._setWatchers.forEach((cb) => cb(oldVal as T));
      }
    }

    return this._cacheVal as T;
  }

  peek() {
    return this._cacheVal as T;
  }

  private _rebuild() {
    this.deps.forEach((dep) => {
      dep.listeners.delete(this as Consumer);
    });
    this.deps.clear();
    activateConsumer(this as Consumer);
    this._cacheVal = this._getter();
    deactivateConsumer();

    return this._cacheVal;
  }

  subscribe(callback: (oldValue: T) => void): () => void {
    this._setWatchers.push(callback);

    return () => {
      this.unsubscribe(callback);
    };
  }

  unsubscribe(callback: (oldValue: T) => void) {
    this._setWatchers = this._setWatchers.filter((cb) => cb !== callback);
  }

  markDirty() {
    this._dirty = true;
    [...this.listeners].forEach((val) => val.markDirty());
  }

  constructor(getter: () => T) {
    this._getter = getter;
    this._rebuild();
  }

  dispose() {
    this.deps.forEach((dep) => {
      dep.listeners.delete(this as Consumer);
    });

    this.deps.clear();
    this.listeners.clear();
    this._setWatchers = [];
  }
}
